import dayjs from 'dayjs';

// Sample Wallets - Colors from design system
export const sampleWallets = {
  basic: [
    {
      id: 'sample_wallet_cash',
      name: 'Tiền mặt',
      type: 'basic',
      icon: 'wallet',
      color: '#5EBD82',  // green[500]
      basicWallet: {
        balance: 5000000,
      },
    },
    {
      id: 'sample_wallet_vietcombank',
      name: 'Thanh toán Vietcombank',
      type: 'basic',
      icon: 'bank',
      color: '#1FE0FF',  // teal[500]
      basicWallet: {
        balance: 3000000,
      },
    },
    {
      id: 'sample_wallet_techcombank',
      name: 'Tiết kiệm Techcombank',
      type: 'basic',
      icon: 'piggy',
      color: '#6C3DD3',  // purple[500]
      basicWallet: {
        balance: 6000000,
      },
    },
  ],
  credit: [
    {
      id: 'sample_wallet_credit_vcb',
      name: 'Thẻ tín dụng',
      type: 'credit',
      icon: 'credit-card',
      color: '#FF8247',  // orange[500]
      creditWallet: {
        balance: -54000000,
        creditLimit: 100000000,
        statementDateInMonth: 15,
        paymentDueDateInMonth: 25,
      },
    },
  ],
  loan: [
    {
      id: 'sample_wallet_loan',
      name: 'Vay nợ',
      type: 'loan',
      icon: 'hand-coins',
      color: '#F476A7',  // pink[500]
      loanWallet: {
        balance: 10200000,
      },
    },
  ],
};

// Sample Categories - Colors from design system
export const sampleCategories = {
  expense: [
    { id: 'cat_food', name: 'Ăn uống', group: 'Chi tiêu hàng ngày', icon: 'food', color: '#1FE0FF' },        // teal[500]
    { id: 'cat_restaurant', name: 'Ăn nhà hàng', group: 'Chi tiêu hàng ngày', icon: 'restaurant', color: '#FF8247' }, // orange[500]
    { id: 'cat_groceries', name: 'Thực phẩm', group: 'Chi tiêu hàng ngày', icon: 'shopping-cart', color: '#5EBD82' }, // green[500]
    { id: 'cat_snacks', name: 'Ăn vặt', group: 'Chi tiêu hàng ngày', icon: 'cookie', color: '#F476A7' },     // pink[500]
    { id: 'cat_coffee', name: 'Cà phê', group: 'Chi tiêu hàng ngày', icon: 'coffee', color: '#3B7651' },     // green[700]
    { id: 'cat_shopping', name: 'Mua sắm', group: 'Mua sắm', icon: 'shopping-bag', color: '#6C3DD3' },       // purple[500]
    { id: 'cat_transport', name: 'Di chuyển', group: 'Di chuyển', icon: 'car', color: '#F9CE46' },           // yellow[500]
    { id: 'cat_utilities', name: 'Điện nước', group: 'Hóa đơn', icon: 'electric', color: '#19B3CC' },        // teal[600]
    { id: 'cat_entertainment', name: 'Giải trí', group: 'Giải trí', icon: 'game', color: '#8964DC' },        // purple[400]
    { id: 'cat_health', name: 'Sức khỏe', group: 'Sức khỏe', icon: 'health', color: '#F8ADCA' },             // pink[300]
  ],
  income: [
    { id: 'cat_salary', name: 'Lương', group: 'Thu nhập', icon: 'money', color: '#1FE0FF' },                 // teal[500]
    { id: 'cat_bonus', name: 'Thưởng', group: 'Thu nhập', icon: 'gift', color: '#F476A7' },                  // pink[500]
    { id: 'cat_investment', name: 'Đầu tư', group: 'Thu nhập', icon: 'chart', color: '#FF8247' },            // orange[500]
    { id: 'cat_other_income', name: 'Thu nhập khác', group: 'Thu nhập', icon: 'coin', color: '#6C3DD3' },    // purple[500]
  ],
};

// Generate sample transactions for the past 3 months
export const generateSampleTransactions = () => {
  const transactions: any[] = [];
  const today = dayjs();

  // Expense transactions patterns
  const expensePatterns = [
    { category: 'cat_food', name: 'Ăn uống', minAmount: 30000, maxAmount: 150000, frequency: 'daily' },
    { category: 'cat_restaurant', name: 'Ăn nhà hàng', minAmount: 200000, maxAmount: 500000, frequency: 'weekly' },
    { category: 'cat_groceries', name: 'Thực phẩm', minAmount: 500000, maxAmount: 1500000, frequency: 'weekly' },
    { category: 'cat_snacks', name: 'Ăn vặt', minAmount: 20000, maxAmount: 100000, frequency: 'daily' },
    { category: 'cat_coffee', name: 'Cà phê', minAmount: 25000, maxAmount: 80000, frequency: 'daily' },
    { category: 'cat_shopping', name: 'Mua sắm', minAmount: 200000, maxAmount: 2000000, frequency: 'monthly' },
    { category: 'cat_transport', name: 'Di chuyển', minAmount: 50000, maxAmount: 300000, frequency: 'weekly' },
    { category: 'cat_utilities', name: 'Điện nước', minAmount: 500000, maxAmount: 1500000, frequency: 'monthly' },
    { category: 'cat_entertainment', name: 'Giải trí', minAmount: 100000, maxAmount: 500000, frequency: 'weekly' },
    { category: 'cat_health', name: 'Sức khỏe', minAmount: 100000, maxAmount: 1000000, frequency: 'monthly' },
  ];

  // Generate 3 months of data
  for (let monthOffset = 0; monthOffset < 3; monthOffset++) {
    const monthStart = today.subtract(monthOffset, 'month').startOf('month');
    const monthEnd = monthOffset === 0 ? today : today.subtract(monthOffset, 'month').endOf('month');
    const daysInRange = monthEnd.diff(monthStart, 'day') + 1;

    // Add salary at the beginning of each month
    transactions.push({
      id: `sample_tx_salary_${monthOffset}`,
      type: 'income',
      amount: 15000000,
      description: 'Lương tháng',
      categoryId: 'cat_salary',
      category: { id: 'cat_salary', name: 'Thu nhập•Lương' },
      walletId: 'sample_wallet_vietcombank',
      wallet: 'Thanh toán Vietcombank',
      transactionDate: monthStart.add(5, 'day').toISOString(),
    });

    // Add bonus occasionally (every other month)
    if (monthOffset % 2 === 0) {
      transactions.push({
        id: `sample_tx_bonus_${monthOffset}`,
        type: 'income',
        amount: 2000000,
        description: 'Thưởng dự án',
        categoryId: 'cat_bonus',
        category: { id: 'cat_bonus', name: 'Thu nhập•Thưởng' },
        walletId: 'sample_wallet_vietcombank',
        wallet: 'Thanh toán Vietcombank',
        transactionDate: monthStart.add(10, 'day').toISOString(),
      });
    }

    // Generate expense transactions
    expensePatterns.forEach((pattern, patternIndex) => {
      let transactionsCount = 0;

      switch (pattern.frequency) {
        case 'daily':
          transactionsCount = Math.floor(daysInRange * 0.7); // 70% of days
          break;
        case 'weekly':
          transactionsCount = Math.floor(daysInRange / 7);
          break;
        case 'monthly':
          transactionsCount = 1;
          break;
      }

      for (let i = 0; i < transactionsCount; i++) {
        const randomDay = Math.floor(Math.random() * daysInRange);
        const transactionDate = monthStart.add(randomDay, 'day');

        // Skip future dates
        if (transactionDate.isAfter(today)) continue;

        const amount = Math.floor(
          Math.random() * (pattern.maxAmount - pattern.minAmount) + pattern.minAmount
        );

        // Round to nearest 1000
        const roundedAmount = Math.round(amount / 1000) * 1000;

        const wallets = ['sample_wallet_cash', 'sample_wallet_vietcombank'];
        const randomWallet = wallets[Math.floor(Math.random() * wallets.length)];

        const category = sampleCategories.expense.find(c => c.id === pattern.category);

        transactions.push({
          id: `sample_tx_${monthOffset}_${patternIndex}_${i}`,
          type: 'expense',
          amount: -roundedAmount,
          description: pattern.name,
          categoryId: pattern.category,
          category: { id: pattern.category, name: `${category?.group}•${pattern.name}` },
          walletId: randomWallet,
          wallet: randomWallet === 'sample_wallet_cash' ? 'Tiền mặt' : 'Thanh toán Vietcombank',
          transactionDate: transactionDate.toISOString(),
        });
      }
    });
  }

  // Sort by date descending
  transactions.sort((a, b) =>
    dayjs(b.transactionDate).valueOf() - dayjs(a.transactionDate).valueOf()
  );

  return transactions;
};

// Calculate summary data from transactions
export const calculateSampleSummary = (transactions: any[]) => {
  const now = dayjs();
  const currentMonthStart = now.startOf('month');
  const currentMonthEnd = now.endOf('month');

  const currentMonthTransactions = transactions.filter(tx => {
    const txDate = dayjs(tx.transactionDate);
    return txDate.isAfter(currentMonthStart) && txDate.isBefore(currentMonthEnd);
  });

  const totalExpense = currentMonthTransactions
    .filter(tx => tx.type === 'expense')
    .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);

  const totalIncome = currentMonthTransactions
    .filter(tx => tx.type === 'income')
    .reduce((sum, tx) => sum + tx.amount, 0);

  const balance = totalIncome - totalExpense;

  // Calculate total assets
  const basicWalletsTotal = sampleWallets.basic.reduce(
    (sum, w) => sum + (w.basicWallet?.balance || 0), 0
  );
  const creditWalletsTotal = sampleWallets.credit.reduce(
    (sum, w) => sum + (w.creditWallet?.balance || 0), 0
  );
  const loanWalletsTotal = sampleWallets.loan.reduce(
    (sum, w) => sum + (w.loanWallet?.balance || 0), 0
  );

  const totalAssets = basicWalletsTotal + creditWalletsTotal + loanWalletsTotal;

  // Calculate category distribution
  const categoryDistribution: Record<string, number> = {};
  currentMonthTransactions
    .filter(tx => tx.type === 'expense')
    .forEach(tx => {
      const categoryName = tx.description || 'Khác';
      categoryDistribution[categoryName] = (categoryDistribution[categoryName] || 0) + Math.abs(tx.amount);
    });

  return {
    totalExpense,
    totalIncome,
    balance,
    totalAssets,
    categoryDistribution,
    transactionCount: currentMonthTransactions.length,
  };
};

// Sample asset fluctuation data (for chart)
export const generateAssetFluctuationData = () => {
  const data = [];
  const today = dayjs();

  // Use fixed seed values for consistent data during onboarding
  const amounts = [54000000, 55100000, 60100000, 58400000, 59100000, 60900000];

  for (let i = 5; i >= 0; i--) {
    const month = today.subtract(i, 'month');

    data.push({
      month: month.startOf('month').toISOString(), // ISO format for proper date parsing
      monthLabel: month.format('MM/YY'),
      amount: amounts[5 - i],
    });
  }

  return data;
};

// Sample statistics data for onboarding (matches StatisticRecord interface)
export const getSampleStatistics = () => {
  return [
    {
      type: 'total-expense',
      value: 4538000,
      previous: 3200000,
      net: 1338000,
      isIncrease: true,
    },
    {
      type: 'total-income',
      value: 17000000,
      previous: 15000000,
      net: 2000000,
      isIncrease: true,
    },
    {
      type: 'net-flow',
      value: 12462000,
      previous: 11800000,
      net: 662000,
      isIncrease: true,
    },
    {
      type: 'money-out',
      value: 5200000,
      previous: 4800000,
      net: 400000,
      isIncrease: true,
    },
    {
      type: 'money-in',
      value: 17500000,
      previous: 15500000,
      net: 2000000,
      isIncrease: true,
    },
    {
      type: 'net-cash-flow',
      value: 12300000,
      previous: 10700000,
      net: 1600000,
      isIncrease: true,
    },
  ];
};

// SVG icons for sample data categories
const SAMPLE_ICONS = {
  food: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z" fill="currentColor"/></svg>',
  transport: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" fill="currentColor"/></svg>',
  shopping: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6-2c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2zm6 16H6V8h2v2c0 .55.45 1 1 1s1-.45 1-1V8h4v2c0 .55.45 1 1 1s1-.45 1-1V8h2v12z" fill="currentColor"/></svg>',
  entertainment: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5S18.67 9 19.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="currentColor"/></svg>',
  utilities: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2.85 11.1l-.85.6V16h-4v-2.3l-.85-.6A4.997 4.997 0 0 1 7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1z" fill="currentColor"/></svg>',
  other: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/></svg>',
  salary: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="currentColor"/></svg>',
  bonus: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z" fill="currentColor"/></svg>',
};

// Sample expense allocation data for pie chart
// Colors from design system: teal, pink, orange, green, purple, yellow
export const getSampleExpenseAllocation = () => {
  return [
    { name: 'Ăn uống', value: 1500000, percentage: 33, color: '#1FE0FF', categoryId: 'cat_food', icon: SAMPLE_ICONS.food },
    { name: 'Di chuyển', value: 800000, percentage: 18, color: '#F476A7', categoryId: 'cat_transport', icon: SAMPLE_ICONS.transport },
    { name: 'Mua sắm', value: 700000, percentage: 15, color: '#FF8247', categoryId: 'cat_shopping', icon: SAMPLE_ICONS.shopping },
    { name: 'Giải trí', value: 600000, percentage: 13, color: '#3B7651', categoryId: 'cat_entertainment', icon: SAMPLE_ICONS.entertainment },
    { name: 'Điện nước', value: 500000, percentage: 11, color: '#6C3DD3', categoryId: 'cat_utilities', icon: SAMPLE_ICONS.utilities },
    { name: 'Khác', value: 438000, percentage: 10, color: '#F9CE46', categoryId: 'cat_other', icon: SAMPLE_ICONS.other },
  ];
};

// Sample income allocation data
// Colors from design system
export const getSampleIncomeAllocation = () => {
  return [
    { name: 'Lương', value: 15000000, percentage: 88, color: '#1FE0FF', categoryId: 'cat_salary', icon: SAMPLE_ICONS.salary },
    { name: 'Thưởng', value: 2000000, percentage: 12, color: '#F476A7', categoryId: 'cat_bonus', icon: SAMPLE_ICONS.bonus },
  ];
};

// Sample trending data for line/bar charts
export const getSampleTrendingData = () => {
  const today = dayjs();
  const data = [];

  for (let i = 6; i >= 0; i--) {
    const date = today.subtract(i, 'month');
    data.push({
      month: date.format('MM/YYYY'),
      label: date.format('T.MM'),
      expense: Math.floor(3500000 + Math.random() * 2000000),
      income: Math.floor(14000000 + Math.random() * 4000000),
    });
  }

  return data;
};

// Sample wallets with proper balance for display - Colors from design system
export const getSampleWalletsForDisplay = () => {
  return [
    {
      id: 'sample_wallet_cash',
      name: 'Tiền mặt',
      type: 'basic',
      icon: 'wallet',
      color: '#5EBD82',  // green[500]
      balance: 5000000,
      currency: 'VND',
    },
    {
      id: 'sample_wallet_vietcombank',
      name: 'Vietcombank',
      type: 'basic',
      icon: 'bank',
      color: '#1FE0FF',  // teal[500]
      balance: 25000000,
      currency: 'VND',
    },
    {
      id: 'sample_wallet_techcombank',
      name: 'Tiết kiệm',
      type: 'basic',
      icon: 'piggy',
      color: '#6C3DD3',  // purple[500]
      balance: 50000000,
      currency: 'VND',
    },
  ];
};

// Sample total asset balance
export const getSampleTotalBalance = () => {
  return {
    total: 80000000,
    change: 5200000,
    changePercent: 6.9,
    isIncrease: true,
  };
};

// Export all sample data as a bundle
export const getSampleData = () => {
  const transactions = generateSampleTransactions();
  const summary = calculateSampleSummary(transactions);
  const assetFluctuation = generateAssetFluctuationData();

  return {
    wallets: sampleWallets,
    categories: sampleCategories,
    transactions,
    summary,
    assetFluctuation,
    statistics: getSampleStatistics(),
    expenseAllocation: getSampleExpenseAllocation(),
    incomeAllocation: getSampleIncomeAllocation(),
    trending: getSampleTrendingData(),
    walletsDisplay: getSampleWalletsForDisplay(),
    totalBalance: getSampleTotalBalance(),
  };
};
